/**
 * Project Service for QA AI Agent
 *
 * Handles all API calls related to projects and integrations
 */

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// ============================================================================
// Types
// ============================================================================

export interface Project {
  id: string;
  name: string;
  key: string;
  description: string | null;
  default_environment: string | null;
  default_browser: string | null;
  settings: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  integrations_count: number;
  members_count: number;
}

export interface ProjectCreate {
  name: string;
  key: string;
  description?: string;
  default_environment?: string;
  default_browser?: string;
  settings?: Record<string, unknown>;
}

export interface ProjectUpdate {
  name?: string;
  description?: string;
  default_environment?: string;
  default_browser?: string;
  settings?: Record<string, unknown>;
  is_active?: boolean;
}

export interface ProjectListResponse {
  projects: Project[];
  total: number;
}

export interface ProjectMember {
  id: string;
  user_id: string;
  username: string;
  email: string;
  role: string;
  created_at: string;
}

export type IntegrationType = 'jira' | 'github' | 'jenkins' | 'slack' | 'microsoft_teams';

export interface Integration {
  id: string;
  project_id: string;
  integration_type: IntegrationType;
  name: string;
  config: Record<string, unknown>;
  is_active: boolean;
  is_verified: boolean;
  last_verified_at: string | null;
  verification_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface IntegrationCreate {
  integration_type: IntegrationType;
  name: string;
  config: Record<string, unknown>;
  credentials: Record<string, unknown>;
}

export interface IntegrationUpdate {
  name?: string;
  config?: Record<string, unknown>;
  credentials?: Record<string, unknown>;
  is_active?: boolean;
}

export interface IntegrationListResponse {
  integrations: Integration[];
  total: number;
}

export interface IntegrationTestResult {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export interface IntegrationTypeInfo {
  type: IntegrationType;
  name: string;
  description: string;
  config_schema: Record<string, unknown>;
  credentials_schema: Record<string, unknown>;
}

// ============================================================================
// Project API Functions
// ============================================================================

/**
 * List all projects
 */
export const listProjects = async (activeOnly = true): Promise<ProjectListResponse> => {
  const response = await fetch(`${API_URL}/projects?active_only=${activeOnly}&page_size=100`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list projects');
  }

  return response.json();
};

/**
 * Get a specific project by ID
 */
export const getProject = async (projectId: string): Promise<Project> => {
  const response = await fetch(`${API_URL}/projects/${projectId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get project');
  }

  return response.json();
};

/**
 * Create a new project
 */
export const createProject = async (project: ProjectCreate): Promise<Project> => {
  const response = await fetch(`${API_URL}/projects`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(project),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to create project');
  }

  return response.json();
};

/**
 * Authorize a domain for testing (admin only).
 *
 * Domain authorization is an org-scoped allowlist owned by the QA Agent: the
 * agent refuses to crawl/test a domain until an org admin attests the org is
 * permitted to. `projectId` is required by the route but the write is
 * org-scoped — authorizing here enables the domain for the whole organization.
 * The backend returns 403 if the caller is not an org admin.
 */
export const authorizeDomain = async (
  projectId: string,
  domain: string,
): Promise<{ success: boolean; domain: string; testing_authorized: boolean }> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/authorize-domain`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ domain }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to authorize domain');
  }

  return response.json();
};

export interface AuthorizedDomain {
  domain: string;
  testing_authorized: boolean;
  authorized_by?: string | null;
  authorized_at?: string | null;
}

/**
 * List domains authorized for testing in the user's organization.
 */
export const listAuthorizedDomains = async (projectId: string): Promise<AuthorizedDomain[]> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/authorized-domains`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list authorized domains');
  }
  return response.json();
};

/**
 * Revoke a domain's testing authorization (admin only).
 */
export const revokeDomain = async (projectId: string, domain: string): Promise<void> => {
  const response = await fetch(
    `${API_URL}/projects/${projectId}/authorized-domains/${encodeURIComponent(domain)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to revoke domain');
  }
};

export interface TestBriefAuth {
  mode: string; // none | token | test_account | otp | real_login
  login_url?: string | null;
  username?: string | null;
  token_location?: string | null;
  secret?: string | null;   // write-only; never returned populated
  secret_set?: boolean;     // read-only: whether a secret is stored
  // OTP
  phone?: string | null;
  otp_source?: string | null;        // device_relay | fixed_code | provider
  otp_device_serial?: string | null;
  otp_fixed_code?: string | null;
  otp_length?: number | null;
  captcha_handling?: string | null;  // test_key | hitl | none
}

export interface TestBriefAccount {
  role: string;
  mode?: string;
  username?: string | null;
  phone?: string | null;
  secret?: string | null;
  secret_set?: boolean;
}

export interface TestBriefApiAuth {
  header?: string | null;
  token?: string | null;    // write-only
  token_set?: boolean;      // read-only
}

export interface TestBriefEnvironment {
  base_url?: string | null;
  api_base_url?: string | null;
  api_auth?: TestBriefApiAuth;
}

export interface TestBrief {
  auth: TestBriefAuth;
  accounts: TestBriefAccount[];
  environment: TestBriefEnvironment;
  guardrails: string[];
  pages_include: string[];
  pages_exclude: string[];
  business_rules: string[];
  assertions: string[];
  seed_notes?: string | null;
  priorities?: string | null;
  notes?: string | null;
}

/** Fetch a project's test brief (auth secret is masked). */
export const getTestBrief = async (projectId: string): Promise<TestBrief> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/test-brief`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to load test brief');
  }
  return response.json();
};

/** Create/update a project's test brief. Leave auth.secret blank to keep the stored one. */
export const saveTestBrief = async (projectId: string, brief: TestBrief): Promise<TestBrief> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/test-brief`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(brief),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to save test brief');
  }
  return response.json();
};

// ---- API/Security Workbench ----

export interface ApiKV { key: string; value: string }
export interface ApiRequest {
  id?: string;
  name: string;
  method: string;
  url: string;
  headers: ApiKV[];
  query: ApiKV[];
  body?: string | null;
  body_type?: string;
  auth?: { type: string; token?: string | null };
  assertions?: { status?: number; contains?: string[] };
  security?: { attacks: string[] };
}
export interface ApiCollection {
  requests: ApiRequest[];
  variables: Record<string, string>;
}

export const getApiWorkbench = async (projectId: string): Promise<ApiCollection> => {
  const r = await fetch(`${API_URL}/projects/${projectId}/api-workbench`, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to load workbench');
  return r.json();
};

export const saveApiWorkbench = async (projectId: string, col: ApiCollection): Promise<ApiCollection> => {
  const r = await fetch(`${API_URL}/projects/${projectId}/api-workbench`, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(col),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to save workbench');
  return r.json();
};

export const importApiWorkbench = async (
  projectId: string,
  payload: { kind: 'curl' | 'openapi'; curl?: string; openapi_url?: string; openapi_json?: any },
): Promise<{ requests: ApiRequest[] }> => {
  const r = await fetch(`${API_URL}/projects/${projectId}/api-workbench/import`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Import failed');
  return r.json();
};

export const sendApiRequest = async (
  projectId: string, request: ApiRequest, variables: Record<string, string>,
): Promise<any> => {
  const r = await fetch(`${API_URL}/projects/${projectId}/api-workbench/send`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ request, variables }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Send failed');
  return r.json();
};

// ---- Record & generate ----

export interface RecordedStep {
  action: string;
  selector?: string;
  value?: string;
  text?: string;
  url?: string;
}

export const startRecording = async (
  startUrl: string, projectId?: string | number | null,
): Promise<{ session_id: string; vnc_url: string; page_loaded?: boolean; expires_in_seconds?: number }> => {
  const r = await fetch(`${API_URL}/recorder/start`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ start_url: startUrl, project_id: projectId ? Number(projectId) : undefined }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not start recording');
  return r.json();
};

export const getRecorderEvents = async (
  sessionId: string,
): Promise<{ steps: RecordedStep[]; count: number; alive?: boolean; expires_in_seconds?: number }> => {
  const r = await fetch(`${API_URL}/recorder/${sessionId}/events`, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'poll failed');
  return r.json();
};

export const recorderNavigate = async (sessionId: string, url: string): Promise<{ page_loaded: boolean }> => {
  const r = await fetch(`${API_URL}/recorder/${sessionId}/navigate`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'reload failed');
  return r.json();
};

export const stopRecording = async (sessionId: string): Promise<{ steps: RecordedStep[] }> => {
  const r = await fetch(`${API_URL}/recorder/${sessionId}/stop`, { method: 'POST', credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'stop failed');
  return r.json();
};

export const closeRecording = async (sessionId: string): Promise<void> => {
  await fetch(`${API_URL}/recorder/${sessionId}/close`, { method: 'POST', credentials: 'include' }).catch(() => {});
};

/**
 * Update an existing project
 */
export const updateProject = async (projectId: string, project: ProjectUpdate): Promise<Project> => {
  const response = await fetch(`${API_URL}/projects/${projectId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(project),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to update project');
  }

  return response.json();
};

/**
 * Delete a project (soft delete)
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/projects/${projectId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to delete project');
  }
};

/**
 * Set the current project context
 */
export const setCurrentProject = async (projectId: string): Promise<{ success: boolean; project_key: string; project_name: string }> => {
  const response = await fetch(`${API_URL}/projects/current`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ project_id: projectId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to set current project');
  }

  return response.json();
};

/**
 * Get the current project context
 */
export const getCurrentProject = async (): Promise<Project | null> => {
  const response = await fetch(`${API_URL}/projects/current/info`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get current project');
  }

  const data = await response.json();
  // The API answers {project_id, project_name, project_key, organization_id}
  // (CurrentProjectResponse), with project_id null when nothing is set. It
  // used to be read as `data.id`, which never exists on this payload, so the
  // server's current project was always discarded and the header showed
  // "No project" while the agent kept running against the stored one.
  if (!data || !data.project_id) {
    return null;
  }
  return {
    id: String(data.project_id),
    name: data.project_name || `Project ${data.project_id}`,
    key: data.project_key || '',
    description: null,
    default_environment: null,
    default_browser: null,
    settings: {},
    is_active: true,
    created_at: '',
    updated_at: '',
    integrations_count: 0,
    members_count: 0,
  };
};

/**
 * Clear the current project context on the server ("No project").
 */
export const clearCurrentProject = async (): Promise<void> => {
  const response = await fetch(`${API_URL}/projects/current`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok && response.status !== 404) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to clear current project');
  }
};

// ============================================================================
// Project Members API Functions
// ============================================================================

/**
 * List project members
 */
export const listProjectMembers = async (projectId: string): Promise<ProjectMember[]> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/members`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list project members');
  }

  return response.json();
};

/**
 * Add a member to a project
 */
export const addProjectMember = async (
  projectId: string,
  userId: string,
  role: string = 'member'
): Promise<ProjectMember> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/members`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId, role }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to add project member');
  }

  return response.json();
};

/**
 * Remove a member from a project
 */
export const removeProjectMember = async (projectId: string, userId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/members/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to remove project member');
  }
};

// ============================================================================
// Integration API Functions
// ============================================================================

/**
 * Get all available integration types
 */
export const getIntegrationTypes = async (): Promise<IntegrationTypeInfo[]> => {
  const response = await fetch(`${API_URL}/integrations/types`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get integration types');
  }

  return response.json();
};

/**
 * Get info about a specific integration type
 */
export const getIntegrationTypeInfo = async (type: IntegrationType): Promise<IntegrationTypeInfo> => {
  const response = await fetch(`${API_URL}/integrations/types/${type}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get integration type info');
  }

  return response.json();
};

/**
 * List integrations for a project
 */
export const listProjectIntegrations = async (
  projectId: string,
  activeOnly = true
): Promise<IntegrationListResponse> => {
  const response = await fetch(`${API_URL}/integrations/project/${projectId}?active_only=${activeOnly}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list integrations');
  }

  return response.json();
};

/**
 * Create a new integration for a project
 */
export const createIntegration = async (
  projectId: string,
  integration: IntegrationCreate
): Promise<Integration> => {
  const response = await fetch(`${API_URL}/integrations/project/${projectId}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(integration),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to create integration');
  }

  return response.json();
};

/**
 * Get an integration by ID
 */
export const getIntegration = async (integrationId: string): Promise<Integration> => {
  const response = await fetch(`${API_URL}/integrations/${integrationId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get integration');
  }

  return response.json();
};

/**
 * Update an integration
 */
export const updateIntegration = async (
  integrationId: string,
  integration: IntegrationUpdate
): Promise<Integration> => {
  const response = await fetch(`${API_URL}/integrations/${integrationId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(integration),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to update integration');
  }

  return response.json();
};

/**
 * Delete an integration
 */
export const deleteIntegration = async (integrationId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/integrations/${integrationId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to delete integration');
  }
};

/**
 * Test an integration connection
 */
export const testIntegration = async (integrationId: string): Promise<IntegrationTestResult> => {
  const response = await fetch(`${API_URL}/integrations/${integrationId}/test`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to test integration');
  }

  return response.json();
};

// ============================================================================
// Local Storage for Project Context
// ============================================================================

const CURRENT_PROJECT_KEY = 'qa_agent_current_project';
const DEFAULT_PROJECT_KEY = 'qa_agent_default_project';

/**
 * Save current project to local storage
 */
export const saveCurrentProjectLocally = (project: Project | null): void => {
  if (project) {
    localStorage.setItem(CURRENT_PROJECT_KEY, JSON.stringify(project));
  } else {
    localStorage.removeItem(CURRENT_PROJECT_KEY);
  }
};

/**
 * Get current project from local storage
 */
export const getLocalCurrentProject = (): Project | null => {
  const stored = localStorage.getItem(CURRENT_PROJECT_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Save default project to local storage
 */
export const saveDefaultProject = (project: Project | null): void => {
  if (project) {
    localStorage.setItem(DEFAULT_PROJECT_KEY, JSON.stringify(project));
  } else {
    localStorage.removeItem(DEFAULT_PROJECT_KEY);
  }
};

/**
 * Get default project from local storage
 */
export const getDefaultProject = (): Project | null => {
  const stored = localStorage.getItem(DEFAULT_PROJECT_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Check if a project is the default project
 */
export const isDefaultProject = (projectId: string): boolean => {
  const defaultProject = getDefaultProject();
  return defaultProject?.id === projectId;
};

// ============================================================================
// Integration Type Metadata
// ============================================================================

export const INTEGRATION_METADATA: Record<IntegrationType, { icon: string; color: string; displayName: string }> = {
  jira: {
    icon: 'jira',
    color: '#0052CC',
    displayName: 'JIRA',
  },
  github: {
    icon: 'github',
    color: '#24292E',
    displayName: 'GitHub',
  },
  jenkins: {
    icon: 'jenkins',
    color: '#D33833',
    displayName: 'Jenkins',
  },
  slack: {
    icon: 'slack',
    color: '#4A154B',
    displayName: 'Slack',
  },
  microsoft_teams: {
    icon: 'microsoft-teams',
    color: '#6264A7',
    displayName: 'Microsoft Teams',
  },
};

const projectService = {
  // Projects
  listProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  setCurrentProject,
  getCurrentProject,
  clearCurrentProject,
  // Members
  listProjectMembers,
  addProjectMember,
  removeProjectMember,
  // Integrations
  getIntegrationTypes,
  getIntegrationTypeInfo,
  listProjectIntegrations,
  createIntegration,
  getIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegration,
  // Local storage
  saveCurrentProjectLocally,
  getLocalCurrentProject,
  saveDefaultProject,
  getDefaultProject,
  isDefaultProject,
  // Metadata
  INTEGRATION_METADATA,
};

export default projectService;
